// ScheduleEditor — the operator write-side of PaymentsCard (OH-124 slice 2,
// spec §5.1): add a row, edit a row inline, mark received, void. Everything
// here is rendered by PaymentsCard ONLY when the caller holds
// payments.schedule_write (I12) — this file assumes that gate already
// happened and does not re-check the permission itself, so it must never be
// imported/rendered from anywhere that skips that check.
//
// Slice 3 (task 23, OH-166 §10) adds the income-side "Confirm receipt" flow
// (finance.confirm, checked by THIS file, unlike the payments.schedule_write
// affordances above — the two permissions are independent) and mutes the
// legacy void/mark-received actions once a row is money_observed or the
// finance-block cutover has run. Edit stays available on such a row (OH-183
// slice-3 tails, item 8) but only through a REDUCED form — the date alone.
// The server refuses amount/method once MoneyObserved
// (booking_payment_schedule.go's guard); the note is not offered because
// the converger rewrites an observed system row's note on its next Sync, so
// an operator note there would silently vanish (owner, 2026-09-29).
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ConfirmReceiptDialog } from '@/components/finance/ConfirmReceiptDialog';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errors';
import { formatDate, formatMoney } from '@/lib/format';
import { INVALID, majorStrToMinorOpt, minorToMajorStr } from '@/lib/pricingRules';
import { PAYMENT_METHOD_LABEL, PAYMENT_STATUS_TONE, type PaymentMethod } from '@/lib/status-tone';
import {
  formatCardStateLine,
  parseCardStateNote,
  useCreateScheduleEntry,
  useUpdateScheduleEntry,
  type PaymentScheduleEntry,
  type SystemDivergence,
  type UpdateScheduleEntryRequest,
} from '@/lib/payments';

// FR-17 (browser smoke S1): the note the converger writes on a direct
// booking's capture row after the cutover (internal/domain/billing/
// generate.go desiredBase, the rules.IncomeSide branch — keep the two
// strings equal). It describes the row's state at birth, and nothing
// rewrites it on settle (the converger leaves received rows alone), so once
// the row is received it would contradict the row. Hidden in the SPA rather
// than cleared by the state functions: display-only, no data change, no
// second writer of the notes column.
const CONVERGER_AWAITING_NOTE = 'captured at booking; awaiting finance confirmation';

const METHOD_OPTIONS = (Object.entries(PAYMENT_METHOD_LABEL) as [PaymentMethod, string][]).map(
  ([value, label]) => ({ value, label }),
);

// Schedule dates are day-granular (checkin+1d / checkout / booking date,
// spec §3.2) — the editor works in plain YYYY-MM-DD and only produces a full
// ISO datetime (UTC midnight) at the API edge.
function dateToISO(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00Z`).toISOString();
}
function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}

interface EntryFormValues {
  // Always a positive magnitude, whatever the method — see signedAmountMinor.
  amountMajor: string;
  date: string;
  method: PaymentMethod;
  notes: string;
}

function emptyForm(): EntryFormValues {
  return {
    amountMajor: '',
    date: new Date().toISOString().slice(0, 10),
    method: 'manual',
    notes: '',
  };
}

function formFromEntry(e: PaymentScheduleEntry): EntryFormValues {
  return {
    amountMajor: minorToMajorStr(Math.abs(e.amount_minor)),
    date: isoToDateInput(e.expected_at),
    method: e.method,
    notes: e.notes ?? '',
  };
}

// Refunds are entered as a plain positive amount — "how much did we give
// back" — and the sign is derived from the chosen method HERE, at submit
// time, never typed by the operator and never read back off the stored
// (already-signed) row. This is the API's own rule enforced both ways
// (validateScheduleAmount in booking_payment_schedule.go): refund <=>
// negative. Deriving the sign from the method means there is no "type a
// minus sign or the API 400s" trap, and switching a row's method away from
// refund cannot leave a stale negative behind.
function signedAmountMinor(magnitudeMinor: number, method: PaymentMethod): number {
  return method === 'refund' ? -Math.abs(magnitudeMinor) : Math.abs(magnitudeMinor);
}

function validateAmount(amountMajor: string): number | null {
  const parsed = majorStrToMinorOpt(amountMajor);
  if (parsed === INVALID || parsed === undefined || parsed === 0) return null;
  return parsed;
}

interface EntryFormProps {
  initial: EntryFormValues;
  submitting: boolean;
  submitLabel: string;
  // amountMinor is null in 'dateOnly' mode — there is nothing to derive it
  // from (the amount input does not render), and the caller must not send
  // amount_minor/method in that case (see ScheduleEntryRow's onSubmit).
  onSubmit: (values: EntryFormValues, amountMinor: number | null) => void;
  onCancel: () => void;
  /**
   * 'dateOnly' (OH-183 slice-3 tails, item 8): the reduced form a
   * money-observed row's Edit opens into — amount/method inputs are not
   * rendered at all, since the server refuses a change to either once the
   * row is observed (booking_payment_schedule.go's guard) and
   * UpdateSystemScheduleEntry freezes both on the row itself; the note is
   * not rendered either (see the file header). Defaults to
   * 'full' — every other caller (Add row, a non-observed row's Edit) is
   * unaffected.
   */
  mode?: 'full' | 'dateOnly';
}

// EntryForm — the shared amount/date/method/note fields for both "add" and
// "edit" (they differ only in initial values and what onSubmit does with the
// result). Money in, money out: the input is a major-unit string the whole
// way through; it is converted to minor units exactly once, on submit.
function EntryForm({ initial, submitting, submitLabel, onSubmit, onCancel, mode = 'full' }: EntryFormProps) {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  function patch(p: Partial<EntryFormValues>) {
    setValues((v) => ({ ...v, ...p }));
  }

  function submit() {
    if (!values.date) {
      setError('Date is required.');
      return;
    }
    if (mode === 'dateOnly') {
      setError(null);
      onSubmit(values, null);
      return;
    }
    const magnitude = validateAmount(values.amountMajor);
    if (magnitude === null) {
      setError('Enter a non-zero amount.');
      return;
    }
    setError(null);
    onSubmit(values, signedAmountMinor(magnitude, values.method));
  }

  return (
    <div className="grid grid-cols-2 gap-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3 sm:grid-cols-4">
      {mode === 'full' && (
        <Input
          label="Amount"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          value={values.amountMajor}
          onChange={(e) => patch({ amountMajor: e.target.value })}
          hint={
            values.method === 'refund'
              ? 'Entered as a positive amount; recorded as a refund (negative).'
              : undefined
          }
        />
      )}
      <Input
        label="Date"
        type="date"
        value={values.date}
        onChange={(e) => patch({ date: e.target.value })}
      />
      {mode === 'full' && (
        <Select
          label="Method"
          options={METHOD_OPTIONS}
          value={values.method}
          onChange={(e) => patch({ method: e.target.value as PaymentMethod })}
        />
      )}
      {mode === 'full' && (
        <Input
          label="Note"
          value={values.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder="optional"
        />
      )}
      {mode === 'dateOnly' && (
        <p className="col-span-full text-xs text-muted">
          Money has been observed for this row — only the date can change here; amount and method are changed in the finance block.
        </p>
      )}
      {error && (
        <p role="alert" className="col-span-full text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="col-span-full flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" variant="secondary" size="sm" loading={submitting} onClick={submit}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

// AddScheduleEntryForm — the "Add row" affordance (spec §5.1). Collapsed to a
// single button until opened, so a booking with nothing to add today (the
// common case) gets no extra visual weight.
export function AddScheduleEntryForm({ bookingId }: { bookingId: string }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const create = useCreateScheduleEntry(bookingId);

  if (!open) {
    return (
      <div className="pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
          Add row
        </Button>
      </div>
    );
  }

  return (
    <EntryForm
      initial={emptyForm()}
      submitting={create.isPending}
      submitLabel="Add row"
      onCancel={() => setOpen(false)}
      onSubmit={(values, amountMinor) => {
        // Add row never passes mode='dateOnly' — amountMinor is always
        // derived here (EntryForm's default 'full' mode is the only one
        // this caller uses).
        if (amountMinor === null) return;
        const notes = values.notes.trim();
        create.mutate(
          {
            amount_minor: amountMinor,
            expected_at: dateToISO(values.date),
            method: values.method,
            // Omitted (not sent as '') when blank — the create schema has no
            // existing value to "clear", so there is only one meaning here.
            ...(notes ? { notes } : {}),
          },
          {
            onSuccess: () => {
              toast({ kind: 'success', title: 'Row added' });
              setOpen(false);
            },
            onError: (err) => {
              toast({ kind: 'error', title: 'Could not add row', message: errorMessage(err) });
            },
          },
        );
      }}
    />
  );
}

// The two divergence reasons (spec §5, OH-124 task 9) must not read the
// same: one names a live row the booking moved under, the other names money
// with NO live row at all (the operator voided it, but the channel rule
// still wants it) — the more urgent of the two, so it renders in the
// destructive tone rather than the warning one.
function DivergenceHint({ divergence, currency }: { divergence: SystemDivergence; currency: string }) {
  if (divergence.reason === 'obligation_unclaimed') {
    return (
      <p className="text-xs text-destructive">
        {
          "Voided, but the channel rule still expects a payment here — nothing is currently claiming this money. Add a row if it's still owed."
        }
      </p>
    );
  }
  const text = `The booking changed after this row was edited — rules now expect ${formatMoney(
    divergence.desired_amount_minor,
    currency,
  )} on ${formatDate(divergence.desired_expected_at.slice(0, 10))}.`;
  return <p className="text-xs text-[var(--ollio-warning,#b45309)]">{text}</p>;
}

// ScheduleEntryRow — one payment-schedule row, read view plus (when
// canWrite) inline edit / mark received / void. PaymentsCard renders one of
// these per entry regardless of permission; the write affordances are the
// only part gated here (I12 — PaymentsCard decides whether canWrite is ever
// true at all, this component just respects it).
export function ScheduleEntryRow({
  bookingId,
  entry,
  canWrite,
  canConfirm = false,
  canReadFinance = false,
  incomeSideEnabled = false,
  businessTz,
  divergence,
}: {
  bookingId: string;
  entry: PaymentScheduleEntry;
  canWrite: boolean;
  /** finance.confirm — gates the "Confirm receipt" button (I12), independent of canWrite. */
  canConfirm?: boolean;
  /**
   * finance.read — gates the refund row's "Track in Finance" link (I12).
   * There is no per-row refund-obligation status on the wire
   * (PaymentScheduleEntry carries no such field, api/openapi.yaml) — the
   * events screen is the source of truth for a refund's own status, so this
   * row only links there rather than rendering a status this endpoint does
   * not actually return.
   */
  canReadFinance?: boolean;
  /** finance settings' income_side_enabled (slice 3 cutover, §13.2) — flips which write path this row offers. */
  incomeSideEnabled?: boolean;
  /**
   * D-M1 (final review): finance settings' business_tz, forwarded to
   * ConfirmReceiptDialog — without it the dialog's default date defaults to
   * the BROWSER's day (todayInput's own comment warns against this), which
   * near midnight in Belgrade can be the wrong side of a month boundary.
   */
  businessTz?: string;
  divergence?: SystemDivergence;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // The date the money actually arrived (OH-124): defaults to today, but the
  // operator recording a bank transfer that landed earlier must be able to
  // back it up — the schedule's received_at is the authority reporting and
  // bookkeeping read, so a click that always meant "today" was a ledger that
  // could not record what actually happened. Plain YYYY-MM-DD, converted to
  // a full ISO instant only at submit (dateToISO) — same idiom as the rest
  // of this card's date fields.
  const [receivedDate, setReceivedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const update = useUpdateScheduleEntry(bookingId);

  const tone = PAYMENT_STATUS_TONE[entry.status];
  const received = entry.status === 'received' && entry.received_at;
  const isRefund = entry.method === 'refund';
  // Card-state notes only exist for booking_com (vcc_charge) rows — other
  // methods never carry this template, so parsing them is skipped rather
  // than risk a false-positive match on an operator's free-text note.
  const cardState = entry.method === 'vcc_charge' ? parseCardStateNote(entry.notes) : null;

  // Money observed (§10.1, OH-166): the row cannot be settled or amount-
  // edited here — it is either already queued as a third-source provider
  // fact or awaiting one — so Void/Mark received do not render at all
  // (I12), replaced by a plain caption. Edit still renders (OH-183 item 8)
  // but opens the reduced date-only form — see showEdit/editMode below.
  //
  // D-I1 (final review, flag-OFF prod regression): the server computes
  // money_observed for EVERY GET regardless of income_side_enabled
  // (booking_payment_schedule_money_observed is unconditional — a linked
  // `received` capture row is money_observed=true even before the cutover),
  // but the server's own PATCH guard only enforces the mute when the flag is
  // ON (booking_payment_schedule.go's guard returns nil first thing when
  // !rules.IncomeSide). Gating on money_observed alone therefore hid
  // Edit/Void/Mark-received on a PayPal-paid direct booking in prod, where
  // income_side_enabled=false until OH-181 and there is no Finance action
  // that can reach the row instead (settle answers 409 income_side_disabled,
  // the queue hides the schedule source pre-cutover). ANDing with the flag
  // mirrors the server's own guard exactly.
  const observed = !!entry.money_observed && incomeSideEnabled;
  const awaitingFinance = incomeSideEnabled && entry.status === 'expected';
  // FR-17 (browser smoke S1): money_observed mutes ONLY the legacy write
  // affordances below — Confirm receipt is exactly the path for a row whose
  // money has been observed (plan Task 23 step 2, docs/30 §13). Gating it on
  // !observed hid it from every capture row that could be settled (a linked
  // capture row is always money_observed) and left no UI path at all. The
  // gate mirrors SettleScheduleEntry's three branches instead:
  //  - capture → only through its captured receipt; without one the server
  //    answers not_a_target, and money_observed is exactly "a receipt exists";
  //  - refund → only a projection of an EXECUTED obligation (not_executed
  //    for an open one — same rule as the Events screen, Ruling FR-14; a
  //    refund row with no obligation is refused outright, 422 not_a_target,
  //    and is closed by Edit → waived/cancelled instead, docs/30 §17 —
  //    Ruling FR-20);
  //  - anything else (payout / vcc_charge / manual) is a plain target.
  const settleable =
    entry.method === 'capture'
      ? !!entry.money_observed
      : entry.method === 'refund'
        ? entry.refund_status === 'executed'
        : true;
  const showConfirmReceipt = canConfirm && awaitingFinance && settleable;
  // "Mark received" is the PRE-cutover write path (ADR-033's original
  // schedule editor); once income_side_enabled the same row is settled
  // through Confirm receipt instead, so this disappears rather than
  // offering two ways to record the same money.
  const showMarkReceived = canWrite && entry.status === 'expected' && !observed && !incomeSideEnabled;
  const showVoid = canWrite && entry.status === 'expected' && !observed;
  // showEdit (OH-183 item 8): an observed row is no longer hidden from Edit
  // entirely — it opens the reduced date-only form instead (editMode
  // below), matching what the server still accepts once money_observed
  // (booking_payment_schedule.go's guard blocks amount/method there, never
  // date/notes).
  //
  // OH-183 item 13: a waived/cancelled row, once income_side_enabled, is
  // settled/void history — the server refuses an amount/method change on
  // it (the same guard, a new branch keyed on current.Status rather than
  // MoneyObserved, since a row can be waived/cancelled with no money ever
  // observed). Rather than open yet another reduced form for a row nobody
  // should be editing at all once it is closed, Edit is hidden outright
  // here — the server still tolerates a notes-only PATCH on it, but this
  // form has no path that sends notes without also touching amount/method
  // by default, so exposing it would only invite a 409 the operator cannot
  // act on usefully.
  const editClosedIncomeSide = incomeSideEnabled && (entry.status === 'waived' || entry.status === 'cancelled');
  const showEdit = canWrite && !editClosedIncomeSide;
  const editMode: 'full' | 'dateOnly' = observed ? 'dateOnly' : 'full';

  function markReceived() {
    update.mutate(
      { entryId: entry.id, body: { status: 'received', received_at: dateToISO(receivedDate) } },
      {
        onSuccess: () => toast({ kind: 'success', title: 'Marked received' }),
        onError: (err) =>
          toast({ kind: 'error', title: 'Could not mark received', message: errorMessage(err) }),
      },
    );
  }

  async function voidEntry() {
    const ok = await askConfirm({
      title: 'Void this schedule row?',
      body: 'The row stays visible as cancelled — there is no delete.',
      destructive: true,
      confirmLabel: 'Void row',
    });
    if (!ok) return;
    update.mutate(
      { entryId: entry.id, body: { status: 'cancelled' } },
      {
        onSuccess: () => toast({ kind: 'success', title: 'Row voided' }),
        onError: (err) => toast({ kind: 'error', title: 'Could not void row', message: errorMessage(err) }),
      },
    );
  }

  if (editing) {
    const initial = formFromEntry(entry);
    return (
      <li className="py-2">
        <EntryForm
          initial={initial}
          submitting={update.isPending}
          submitLabel="Save"
          mode={editMode}
          onCancel={() => setEditing(false)}
          onSubmit={(values, amountMinor) => {
            // method + amount_minor always travel together, deliberately:
            // the server validates the RESULTING pair (validateScheduleAmount
            // reads the patched method against the patched amount), so a
            // lone method flip against a stored sign would 400. In
            // 'dateOnly' mode (OH-183 item 8) amountMinor is null — neither
            // field is sent at all (nor notes, which that mode never renders), so the server's overlay keeps the row's
            // existing amount/method untouched (and, since neither changed,
            // its origin-preserve rule for an already-observed row applies).
            const body: UpdateScheduleEntryRequest = {};
            if (amountMinor !== null) {
              body.method = values.method;
              body.amount_minor = amountMinor;
            }
            // expected_at and notes are only sent when actually changed.
            // expected_at: re-sending a value seeded via .slice(0,10) would
            // truncate any real wall-clock timestamp (e.g. a direct-with-hold
            // capture's created_at) to UTC midnight on every edit, even one
            // that only touched the note.
            if (values.date !== initial.date) body.expected_at = dateToISO(values.date);
            // notes: omitting leaves the stored note unchanged; '' is how the
            // operator clears it (server normalizes to NULL) — those are
            // different operations and must not collapse into one.
            const trimmedNotes = values.notes.trim();
            if (editMode === 'full' && trimmedNotes !== initial.notes.trim()) body.notes = trimmedNotes;
            update.mutate(
              { entryId: entry.id, body },
              {
                onSuccess: () => {
                  toast({ kind: 'success', title: 'Row updated' });
                  setEditing(false);
                },
                onError: (err) => toast({ kind: 'error', title: 'Could not save', message: errorMessage(err) }),
              },
            );
          }}
        />
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-1 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {isRefund ? (
              <Badge variant="danger" size="sm">
                {PAYMENT_METHOD_LABEL[entry.method]}
              </Badge>
            ) : (
              <span className="font-medium">{PAYMENT_METHOD_LABEL[entry.method]}</span>
            )}
            <Badge variant={tone.variant} size="sm">
              {tone.label}
            </Badge>
            {entry.overdue && (
              <Badge variant="danger" size="sm">
                Overdue
              </Badge>
            )}
            {entry.origin === 'operator' && (
              <Badge variant="neutral" size="sm">
                Operator
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted">
            {/* Schedule dates are day-granular; the fields are full ISO
                datetimes, so slice the date part — formatDate expects
                YYYY-MM-DD and would not parse a full timestamp. */}
            {received
              ? `Received ${formatDate((entry.received_at as string).slice(0, 10))}`
              : `Expected ${formatDate(entry.expected_at.slice(0, 10))}`}
          </p>
          {cardState ? (
            <p className="text-xs text-muted">{formatCardStateLine(cardState)}</p>
          ) : (
            entry.notes &&
            !(received && entry.notes === CONVERGER_AWAITING_NOTE) && (
              <p className="text-xs text-muted">{entry.notes}</p>
            )
          )}
          {/* FR-17: these two captions describe a row the finance block has
              yet to confirm — shown while it is expected after the cutover,
              whether or not money_observed (a linked capture row always is).
              Before the cutover there is no such state (the pre-slice card
              never showed them); once received they would be stale. */}
          {awaitingFinance && entry.method === 'capture' && entry.guest_paid_at && (
            <p className="text-xs text-muted">Paid by guest · awaiting confirmation</p>
          )}
          {awaitingFinance && entry.method === 'vcc_charge' && entry.card_charged_at && (
            <p className="text-xs text-muted">Charged from card · awaiting payout</p>
          )}
          {entry.received_movement && (
            <p className="text-xs text-muted">
              <Link
                to="/finance/movements/$id"
                params={{ id: entry.received_movement.id }}
                className="text-primary hover:underline"
              >
                {formatMoney(entry.received_movement.amount_minor, entry.received_movement.currency)} via{' '}
                {entry.received_movement.account_name} on {formatDate(entry.received_movement.occurred_on)}
              </Link>
            </p>
          )}
          {observed && entry.status === 'expected' && (
            <p className="text-xs text-muted">Money observed — confirm or reverse in Finance</p>
          )}
          {/* D-I1 (final review, part of FR-3 C-I1): a refund row's own
              obligation state, exposed cheaply by PART 1 on
              PaymentScheduleEntry.refund_status. Only 'executed' gets a
              label — 'open'/'written_off'/'superseded' say nothing an
              operator can act on from this card (the events screen is the
              source of truth), so they render no extra line here. FR-17:
              only while the row is still expected — a received row is
              already confirmed. */}
          {isRefund && entry.refund_status === 'executed' && entry.status === 'expected' && (
            <p className="text-xs text-muted">Refunded · awaiting finance confirmation</p>
          )}
          {isRefund && canReadFinance && (
            <p className="text-xs">
              <Link to="/finance/events" className="text-primary hover:underline">
                Track in Finance → Reconciliation
              </Link>
            </p>
          )}
          {divergence && <DivergenceHint divergence={divergence} currency={entry.currency} />}
        </div>
        <span className={cn('font-semibold tabular-nums', isRefund && 'text-destructive')}>
          {formatMoney(entry.amount_minor, entry.currency)}
        </span>
      </div>
      {(showEdit || showVoid || showMarkReceived || showConfirmReceipt) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {showEdit && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
          {showMarkReceived && (
            <>
              <Input
                label="Received date"
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                // The API rejects a future received_at; capping the native
                // picker at today (UTC — see the note above the state) keeps
                // an obvious typo from ever reaching the round trip, at the
                // cost of not being able to express "today" through the
                // widget during the few-hours-wide UTC-vs-local-midnight gap
                // for a non-UTC operator — a pre-existing property-timezone
                // gap in this whole card (dates here are UTC midnight, not
                // property-local), not something this control introduces.
                max={new Date().toISOString().slice(0, 10)}
                className="w-36"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={update.isPending}
                onClick={markReceived}
              >
                Mark received
              </Button>
            </>
          )}
          {showVoid && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={update.isPending}
              onClick={() => void voidEntry()}
            >
              Void
            </Button>
          )}
          {showConfirmReceipt && (
            <Button type="button" variant="secondary" size="sm" onClick={() => setConfirming(true)}>
              Confirm receipt
            </Button>
          )}
        </div>
      )}
      {confirming && (
        <ConfirmReceiptDialog
          open
          onClose={() => setConfirming(false)}
          bookingId={bookingId}
          entry={entry}
          businessTz={businessTz}
        />
      )}
    </li>
  );
}
